/**
 * LowBalanceBanner — admin/owner-only low-runway nudge.
 *
 * Standard users never see this banner (they can't top up under
 * freemium). Admins/owners see it when the workspace pot dips below
 * `account.low_balance_days` (default 7) of runway OR when there's no
 * burn data and balance drops below 200.
 *
 * Copy leans on the "keep flows uninterrupted" frame rather than
 * "you're about to hit a wall" so it reads as helpful, not adversarial.
 *
 * Dismissal: per-session via sessionStorage. Re-shows next session if
 * still low.
 *
 * Mounted at the top of UserShell + AdminShell (below the persistent
 * verify-email banner, above main content). The UserShell mount is a
 * no-op for standard users (short-circuit on role).
 */

import { useEffect, useState } from 'react';

import { useSession } from '../hooks/useSession.tsx';
import { useBuyCredits } from './BuyCreditsProvider.tsx';

interface WalletPayload {
  ok: boolean;
  account: {
    token_balance_cached_credits: string;
    avg_daily_burn_credits_14d: number;
    runway_days: number | null;
    /** Push 70b (D-198): canonical single knob from
     *  sys__account.settings.low_balance_days. Wallet endpoint clamps to
     *  [1, 90] before returning. */
    low_balance_days: number;
  };
}

const ADMIN_RUNWAY_THRESHOLD_DAYS_FALLBACK = 7;
// Fallback absolute-credits floor — fires only when runway_days is null
// (new/dormant workspace with no 14-day burn history). Deliberately
// small: a fresh 500-credit top-up shouldn't nag; a bottomed-out pot
// with < 200 credits and no burn history probably needs attention.
const ADMIN_FALLBACK_BALANCE_FLOOR = 200;

function dismissKey(role: string): string {
  return `olytic:low-balance-banner-dismissed:${role}`;
}

function num(s: string | null | undefined): number {
  if (s === null || s === undefined || s === '') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export function LowBalanceBanner() {
  const { user, status } = useSession();
  const { open } = useBuyCredits();
  const [wallet, setWallet] = useState<WalletPayload | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (user === null) return false;
    try {
      return window.sessionStorage.getItem(dismissKey(user.role)) === '1';
    } catch {
      return false;
    }
  });

  // Reset dismissal when the role changes (e.g. gear-flip into admin mode
  // from a standard-user session).
  useEffect(() => {
    if (user === null) return;
    try {
      setDismissed(window.sessionStorage.getItem(dismissKey(user.role)) === '1');
    } catch {
      /* ignore */
    }
  }, [user?.role]);

  // Push 70b (D-198): banner is admin/owner only. Standard-user render
  // path short-circuits before any fetch/render work.
  const isPrivileged = user !== null && (user.role === 'owner' || user.role === 'admin');

  // Event-driven wallet refresh — mirrors ChatCreditPill / CreditThresholdBanner
  // (mount + visibilitychange + focus + olytic:wallet-changed). No polling.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    if (!isPrivileged) return undefined;
    let cancelled = false;
    async function load(): Promise<void> {
      if (document.visibilityState === 'hidden') return;
      try {
        const resp = await fetch('/api/me/wallet', { credentials: 'include' });
        if (!resp.ok) return;
        const data = (await resp.json()) as WalletPayload;
        if (!cancelled) setWallet(data);
      } catch {
        /* silent */
      }
    }
    void load();
    function onRefresh(): void {
      void load();
    }
    document.addEventListener('visibilitychange', onRefresh);
    window.addEventListener('focus', onRefresh);
    window.addEventListener('olytic:wallet-changed', onRefresh);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onRefresh);
      window.removeEventListener('focus', onRefresh);
      window.removeEventListener('olytic:wallet-changed', onRefresh);
    };
  }, [status, isPrivileged]);

  if (user === null || !isPrivileged) return null;
  if (wallet === null) return null;
  if (dismissed) return null;

  const decision = decideAdminBanner(wallet);
  if (!decision.fire) return null;

  function handleDismiss(): void {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(dismissKey(user!.role), '1');
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: 'var(--olytic-amber-50)',
        borderBottom: '1px solid var(--olytic-amber-200)',
        color: 'var(--olytic-amber-900)',
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="var(--olytic-amber)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <path d="M8 2 L14 13.5 H2 Z" />
        <line x1="8" y1="6.5" x2="8" y2="10" />
        <circle cx="8" cy="12" r="0.5" fill="var(--olytic-amber)" />
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{decision.headline}</span>{' '}
        <span>{decision.subhead}</span>
      </div>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => open('low_balance')}
      >
        Top up
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss low balance notice"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--olytic-amber-900)',
          opacity: 0.6,
          cursor: 'pointer',
          fontSize: 18,
          padding: '0 4px',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------
// Pure decision helper — kept separate from the component so the branch
// is testable in isolation + so the render code stays short.
// ---------------------------------------------------------------------

interface BannerDecision {
  fire: boolean;
  headline: string;
  subhead: string;
}

/**
 * Admin/owner banner decision — reads the workspace credit pot. Fires
 * when runway_days < wallet.account.low_balance_days (workspace-configured,
 * default 7) OR (no burn data + balance < fallback floor).
 */
function decideAdminBanner(wallet: WalletPayload): BannerDecision {
  const balance = num(wallet.account.token_balance_cached_credits);
  const runway = wallet.account.runway_days;
  const threshold =
    wallet.account.low_balance_days ?? ADMIN_RUNWAY_THRESHOLD_DAYS_FALLBACK;

  if (runway !== null && runway < threshold) {
    const headline = 'Workspace credits are running low.';
    let subhead: string;
    if (runway <= 2) {
      subhead = `Just ${runway} ${runway === 1 ? 'day' : 'days'} of usage remaining. Top up before flows start dropping.`;
    } else {
      subhead = `About ${runway} days of usage remaining at your recent pace.`;
    }
    return { fire: true, headline, subhead };
  }

  if (runway === null && balance < ADMIN_FALLBACK_BALANCE_FLOOR) {
    return {
      fire: true,
      headline: 'Workspace credits are running low.',
      subhead: `Only ${Math.floor(balance).toLocaleString()} credits remaining. Top up before starting.`,
    };
  }

  return { fire: false, headline: '', subhead: '' };
}
